"use client";

import { Role } from "@prisma/client";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useRef } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
// import IconDocument from "@/app/(components)/generics/Icons/IconDocument";
import IconGift from "@/app/(components)/generics/Icons/IconGift";
import IconHeadphones from "@/app/(components)/generics/Icons/IconHeadphones";
import IconHome from "@/app/(components)/generics/Icons/IconHome";
// import IconMagnifyingGlass from "@/app/(components)/generics/Icons/IconMagnifyingGlass";
import IconMessageSequence from "@/app/(components)/generics/Icons/IconMessageSequence";
import IconSettings from "@/app/(components)/generics/Icons/IconSettings";
import IconSidebarAlt from "@/app/(components)/generics/Icons/IconSidebarAlt";
// import IconTabletWriting from "@/app/(components)/generics/Icons/IconTabletWriting";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import IconUsers from "@/app/(components)/generics/Icons/IconUsers";
// import IconWorld from "@/app/(components)/generics/Icons/IconWorld";
import Logo from "@/app/(components)/generics/Logo/Logo";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import SidebarPlan from "@/app/(components)/specifics/navigation/Sidebar/components/SidebarPlan/SidebarPlan";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useBrand,
  useCanManageCompany,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { useIsomorphicLayoutEffect } from "@/app/hooks/default/useIsomorphicLayoutEffect";
import { ModalStore } from "@/app/stores/ModalStore";
import { SidebarStore, useSidebarOpen } from "@/app/stores/SidebarStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { Routes } from "@/app/vars/Routes";
import IconAccount from "../../../generics/Icons/IconAccount";
import SidebarAccount from "./components/SidebarAccount/SidebarAccount";
import SidebarAffiliation from "./components/SidebarAffiliation/SidebarAffiliation";
import SidebarHelp from "./components/SidebarHelp/SidebarHelp";
import SidebarNavItem from "./components/SidebarNavItem";
import SidebarProjects from "./components/SidebarProjects/SidebarProjects";
import styles from "./Sidebar.module.css";

type Props = DefaultProps & {
  isLogged?: boolean;
  initialOpen: boolean;
};

export default function Sidebar(props: Props) {
  const T = useT();
  const lang = props.params.lang;
  const pathname = usePathname();
  const router = useRouter();

  const isOpen = useSidebarOpen(props.initialOpen);
  const onInit = SidebarStore((state) => state.onInit);
  const closeSidebar = SidebarStore((state) => state.closeSidebar);
  const toggleSidebar = SidebarStore((state) => state.toggleSidebar);
  const closeSidebarOnMobile = SidebarStore((state) => state.closeSidebarOnMobile);

  const handleSignIn = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    ModalStore.getState().openSignIn(props.params);
    closeSidebarOnMobile();
  };

  const user = useAuthUser();
  const isAdmin = user?.role === Role.ADMIN || user?.role === Role.SUPER_ADMIN;
  const isCompanyManager = useCanManageCompany();
  const isCompanyOwner = useIsCompanyOwner();
  const isTenant = useBrand() !== null;

  // On a subdomain, access is governed by the company role only, not the global Meidly role.
  const showAdministration = isTenant ? isCompanyManager : isAdmin || isCompanyManager;
  // Affiliation is personal on the main domain; on a subdomain it belongs to the company owner.
  const showAffiliation = !isTenant || isCompanyOwner;
  // On a subdomain, owner and admins see the credit balance; plain members do not.
  const showCredits = !isTenant || isCompanyManager;

  useIsomorphicLayoutEffect(() => {
    onInit();
  }, [onInit]);

  const lastPathname = useRef(pathname);

  useEffect(() => {
    if (lastPathname.current === pathname) {
      return;
    }
    lastPathname.current = pathname;

    if (window.innerWidth >= Limits.SIDEBAR_MOBILE_MAX_WIDTH) {
      return;
    }

    document.body.style.overflowY = "";
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const applyScrollLock = () => {
      const isMobile = window.innerWidth < Limits.SIDEBAR_MOBILE_MAX_WIDTH;
      document.body.style.overflowY = isOpen && isMobile ? "hidden" : "";
    };

    const onResize = () => {
      closeSidebarOnMobile();
      applyScrollLock();
    };

    applyScrollLock();

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, closeSidebarOnMobile]);

  const navItems = [
    ...(user
      ? []
      : [
          {
            label: TKey.HOME,
            href: Routes.HOME,
            icon: <IconHome />,
            isProtected: false,
          },
        ]),
    {
      label: user ? TKey.SIDEBAR_TOOLS_TITLE : TKey.SIDEBAR_OUR_TOOLS_TITLE,
      isTitle: true,
      isProtected: true,
    },
    {
      label: TKey.CALL_ANALYSIS,
      href: Routes.PROTECTED.CALL_ANALYSIS,
      icon: <IconHeadphones />,
      isProtected: true,
    },
    {
      label: TKey.OFFERS_NAV,
      href: Routes.PROTECTED.OFFERS,
      icon: <IconGift />,
      isProtected: true,
    },
    {
      label: TKey.CLIENT_AVATAR_NAV,
      href: Routes.PROTECTED.CLIENT_AVATAR,
      icon: <IconUser />,
      isProtected: true,
    },
    {
      label: TKey.PROSPECT_SEARCH_NAV,
      href: Routes.PROTECTED.PROSPECT_SEARCH,
      icon: <IconUsers />,
      isProtected: true,
    },
    {
      label: TKey.MESSAGES_CONTENT_NAV,
      href: Routes.PROTECTED.MESSAGES_CONTENT,
      icon: <IconMessageSequence />,
      isProtected: true,
    },
    // {
    //   label: TKey.SALES_SCRIPT_NAV,
    //   href: "#",
    //   icon: <IconDocument />,
    //   isProtected: true,
    //   isDisabled: true,
    // },
    // {
    //   label: TKey.CONTENT_CREATION_NAV,
    //   href: "#",
    //   icon: <IconTabletWriting />,
    //   isProtected: true,
    //   isDisabled: true,
    // },
    // {
    //   label: TKey.MARKET_ANALYSIS_NAV,
    //   href: "#",
    //   icon: <IconWorld />,
    //   isProtected: true,
    //   isDisabled: true,
    // },
    // {
    //   label: TKey.COMPETITION_NAV,
    //   href: "#",
    //   icon: <IconMagnifyingGlass />,
    //   isProtected: true,
    //   isDisabled: true,
    // },
  ];

  return (
    <div
      className={UStyles.classNames(
        styles.wrapper,
        props.isLogged ? styles.logged : "",
        !isOpen ? styles.closedWrapper : "",
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeSidebar();
        }
      }}
    >
      <div
        className={UStyles.classNames(
          styles.container,
          props.isLogged ? styles.logged : "",
          !isOpen ? styles.closed : "",
        )}
      >
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <Logo
              light={true}
              min={!isOpen}
              onClick={() => {
                if (isOpen) {
                  router.push(URLs.pathOf(Routes.HOME));
                } else {
                  toggleSidebar();
                }
                closeSidebarOnMobile();
              }}
            />
          </div>

          <button
            className={styles.toggleBtn}
            aria-label={T(TKey.SIDEBAR_TOGGLE)}
            onClick={toggleSidebar}
          >
            <IconSidebarAlt />
          </button>
        </div>

        {showAdministration && (
          <div className={styles.adminNav}>
            <SidebarNavItem
              params={props.params}
              lang={lang}
              label={TKey.ADMINISTRATION}
              href={Routes.PROTECTED.ADMINISTRATION}
              icon={<IconSettings />}
              isActive={URLs.isPathActive(pathname, Routes.PROTECTED.ADMINISTRATION, Routes.HOME)}
              isOpen={isOpen}
              isLogged={!!props.isLogged}
              onNavigate={closeSidebarOnMobile}
            />
          </div>
        )}

        {<SidebarProjects params={props.params} isOpen={isOpen} />}

        <div className={styles.content}>
          <div className={styles.nav}>
            {navItems.map((item, index) => {
              if ((item as { isTitle?: boolean }).isTitle) {
                return isOpen ? (
                  <div key={index} className={styles.navTitle}>
                    {T(item.label)}
                  </div>
                ) : null;
              }

              const isActive = item.href
                ? URLs.isPathActive(pathname, item.href, Routes.HOME)
                : false;

              return (
                <SidebarNavItem
                  key={index}
                  params={props.params}
                  lang={lang}
                  label={item.label}
                  href={item.href}
                  icon={item.icon}
                  isActive={isActive}
                  isOpen={isOpen}
                  isLogged={!!props.isLogged}
                  onNavigate={closeSidebarOnMobile}
                />
              );
            })}
          </div>
        </div>

        <div className={styles.footer}>
          {
            <div className={styles.footerTop}>
              <SidebarHelp lang={lang} isOpen={isOpen} />
            </div>
          }

          {
            <div
              className={styles.footerContent}
              style={!showCredits && !showAffiliation ? { paddingTop: 0 } : undefined}
            >
              {showCredits && <SidebarPlan params={props.params} isOpen={isOpen} />}
              {showAffiliation && <SidebarAffiliation params={props.params} isOpen={isOpen} />}
            </div>
          }

          <div className={styles.footerBottom}>
            {user ? (
              <SidebarAccount user={user} lang={lang} isCollapsed={!isOpen} />
            ) : isOpen ? (
              <div className={styles.signInSection}>
                <Button
                  min={false}
                  params={props.params}
                  text={T(TKey.LOG_IN)}
                  isLink={false}
                  type={ButtonType.SOFT}
                  full={true}
                  onClick={handleSignIn}
                />
              </div>
            ) : (
              <div className={styles.signInSection}>
                <Tooltip
                  position={TooltipPosition.RIGHT}
                  gap={Limits.SIDEBAR_TOOLTIP_GAP}
                  type={TooltipType.GREEN}
                  content={T(TKey.LOG_IN)}
                  className={styles.fullWidth}
                  trigger={
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        min={false}
                        params={props.params}
                        isLink={false}
                        type={ButtonType.GHOST}
                        iconOnly={true}
                        beforeIcon={<IconAccount />}
                        full={false}
                        ariaLabel={T(TKey.LOG_IN)}
                        onClick={handleSignIn}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
